// BRIEF 118 §8 — THE TYPE COLUMN, AS A NUMBER THAT EXISTS.
//
// ── WHAT THE MEASUREMENT REFUTED ────────────────────────────────────────────
//
// §8 says: "Measure the task column's width and reuse THAT number from its own
// definition (do not copy a literal)." There was no such number. MEASURED on
// watt's CaseView tasks table (1600px viewport, en):
//
//     <th> declares width 38   (CaseTasksSection.tsx:513@06b19bac)
//     <td> declares width 38   (CaseTaskRow.tsx:429@06b19bac)
//     the column RENDERS 60    (computed width, every row)
//     the chips inside are 40 ("ADMIN") and 28 ("CCL")
//
// So the declared 38 governs nothing, the rendered 60 is written down nowhere,
// and the chips are not what sets it. The 60 is the HEADER's own content — the
// word "Type" plus its sort indicator — which is 117 §1's finding on the level
// column arriving one column to the left ("the 30 is the header's own sort
// control"). It is locale-stable for the three languages this app ships:
// `case.tk.col.type` is "Type" in en, fr and nl alike.
//
// Reusing "that number from its own definition" therefore meant CREATING the
// definition the brief assumed: one constant, at the width the task column
// already renders, cited by the `<th>`, the `<td>` and every event surface. The
// task table is unchanged by construction — 60 is what it measures today — and
// the event badges gain the column they never had.
//
// ── THE ASYMMETRY, NAMED ────────────────────────────────────────────────────
//
// A `<td width>` is a suggestion: an auto-layout table still grows a column
// past it for wider content, so the task side keeps 60 as a FLOOR. A flex box
// has no such generosity, so the event side is a CAP — which is what §8 asks
// for in as many words ("a code longer than the box ellipsises rather than
// widening"). The two agree at 60 and would drift only if a firm's task-type
// codes rendered wider than its own "Type" header, at which point the table
// grows and the cards do not.

// ── BRIEF 139 §1 — AND WHAT THE MEASUREMENT REFUTED IN TURN ─────────────────
//
// The 60 above is the HEADER's width, and §8's own census says so in as many
// words ("the word 'Type' plus its sort indicator"). MEASURED at 781c54cd, this
// component has three render sites and ALL THREE ARE EVENTS
// (CaseEventRow.tsx:397 and :827, AgendaView.tsx:618); the tasks table never
// renders it and reads only the constant. So an event CARD was reserving 60px
// for a column header it does not have, and a `REU` chip left ~30px of trailing
// dead air on every row, forever.
//
// [RULED, Laurent, 2026-08-26] the event surfaces reserve the width of the
// widest CHIP the tenant's own non-archived event taxonomy can show — so a firm
// that creates "CEREMONY" widens every event surface at once, and archives it to
// narrow them again.
//
// THE MECHANISM IS A MIRROR, NOT A MEASUREMENT. The box renders the longest code
// it could ever hold, invisibly, in the SAME one-cell grid as the real chip; the
// box takes the width of the wider child and the hidden one never paints. No
// `getBoundingClientRect`, no `ResizeObserver`, no canvas `measureText`, no `ch`
// units (`etTag` declares no `fontFamily`, so character-count arithmetic has
// nothing to arithmetic against). It is correct at any zoom, survives a late
// font load, and costs no layout pass. 118 §7's Dashboard mirror is the same
// mechanism, and its sentence is the reason the spacer carries the SAME `etTag`
// box the real chip does: "so the mirror cannot drift from what it mirrors".
//
// THREE THINGS THE PROBE SETTLED before a line of this was written, because a
// grid changes what `display` a child computes to:
//   · WITHOUT `justify-items: start` the chip BLOCKIFIES and STRETCHES to the
//     reserved width — measured: chip 46px inside a 46px box holding a 32px
//     code, i.e. the 15% wash painted across the dead air it was meant to
//     remove. The alignment is load-bearing, not decoration.
//   · `grid-template-columns: minmax(0, CAP)` is NOT a cap on an inline grid —
//     the track takes its growth limit whatever the content is (measured: 120px
//     for a box whose content was 68px). The cap lives on the CELLS.
//   · with the cap on the cells, a code wider than it ELLIPSISES (the chip's own
//     `maxWidth: 100%`, App.tsx) instead of being clipped by the box — which is
//     what the fixed width used to give, and had to be kept.

import type { CSSProperties, ReactNode } from "react";
import { FS, RAD } from "../lib/tokens"; // 204 §C.0 — the chip's size and radius

/**
 * The CV/CCV tasks table's HEADER width — the word "Type" plus its sort
 * indicator (118 §8's census). It is what that constant always measured, and
 * after 139 §1 it is all it measures: the table keeps it as its floor
 * (CaseTasksSection.tsx's `<th>`, CaseTaskRow.tsx's `<td>`), and no event
 * surface reads it any more.
 */
export const TYPE_COL_W = 60;

/**
 * BRIEF 139 §1 — the cap the derived width cannot pass, so one absurd code
 * cannot push the column off a card. TWICE the header floor, rather than a
 * fresh literal: at the chip's own `FS.s9_5` and `2px 6px` padding, 118 §8's
 * census measured "ADMIN" at 40px and "CCL" at 28px — about 5.5px a character
 * plus 12px of padding — so 120px is roughly nineteen characters. "CEREMONY",
 * the ruling's own example, is eight. Past it the chip ellipsises, which is
 * exactly what the fixed width gave before.
 */
export const TYPE_COL_MAX_W = TYPE_COL_W * 2;

/**
 * C30's compact-column width, unchanged and kept beside its sibling so the two
 * are read together: the pop-out is 330px wide and gives the type 52.
 */
export const TYPE_COL_W_REDUCED = 52;

/** The type chip — the matrix card's own, unchanged; also the mirror's body.
 *  204 §C.0 — MOVED here from EisenTaskCard.tsx byte for byte (a move, not an
 *  improvement — the two are two commits), because 204 §C puts the same chip on
 *  the Costs & Fees activity headers and a table header wearing a fifth copy of
 *  this shape is what the brief forbids. The chip's box lives in this file
 *  already; its dress now does too. The other three inline copies of the shape
 *  (CaseTaskRow's main-window tag, AgendaView's, ArchiveView's) are NOT
 *  repointed — restyling them was never asked (CaseTaskRow's own comment). */
export const TYPE_CHIP: CSSProperties = {
  /* §5a — `etTag`'s size AND its padding. */
  fontSize: FS.s9_5,
  padding: "2px 6px",
  borderRadius: RAD.r3,
  background: "var(--as)",
  color: "var(--tp)",
  fontFamily: "'JetBrains Mono',monospace",
  fontWeight: 600,
  flexShrink: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/* 139 §1 — the two children share one cell, so the box is as wide as the wider
   of them. The cap lives HERE and not on the box (see the header): a cell's own
   `max-width` clamps its max-content contribution, which is what sizes the
   track, and it is also what the chip's `maxWidth: 100%` then resolves against
   — so an over-long code ellipsises rather than being clipped by the box.
   `display: flex` and not `block`: a block cell would give the chip a line box
   of its own, which is the descender trap the note below already names.

   AND NO `overflow: hidden` ON THE CELL, WHICH THE FIRST VERSION HAD AND THE
   DRIVE CAUGHT. A grid or flex item whose `overflow` is not `visible` has an
   AUTOMATIC MINIMUM SIZE OF ZERO. With the cells at zero the track's minimum is
   zero, so this whole box's min-content is zero — and `.event-title` is
   `flex-wrap: nowrap` at rest (137 §1), so every item on that line competes and
   the click target wrapping this box (CaseEventRow.tsx:404) is an ordinary
   shrinkable flex item with `min-width: auto`. It collapsed AROUND the box.
   MEASURED on the CaseView agenda pane with the cells clipping: rows at
   42.9 / 40.8 / 34.1 / 33.5, an INTRO chip 9.5px wider than the wrapper holding
   it, and four different title x's — the reservation's whole purpose lost on
   the surface that needed it most. Reproduced in isolation: the wrapper came
   out 6.6px around a 43px box.
   `main` was immune for a reason that left with the width: a DEFINITE `width`
   is the specified size suggestion, so the old rigid 60 was itself the floor.
   Without the clip the cell's automatic minimum is its min-content CLAMPED BY
   `max-width` — so the cap still holds at 120 and an over-long code still
   ellipsises (the chip clips ITSELF; it always did), while the box can no
   longer be squeezed below the code it is showing. The outer box keeps its own
   `overflow: hidden`, which is where clipping belonged all along. */
const CELL = {
  gridArea: "1 / 1",
  display: "flex",
  alignItems: "center",
  maxWidth: TYPE_COL_MAX_W,
} as const;

/**
 * The event surfaces' type box: the chip sits inside it, so everything after it
 * starts at one x down the list. The chip itself is `etTag`'s (App.tsx), which
 * ellipsises inside a box it cannot fill.
 *
 * `mirror` is the SAME `etTag` chip, carrying the longest code the taxonomy can
 * show (`longestTaxonomyCode`, resolved once in App and threaded on the path
 * `etTag` already travels). Absent, the box simply wraps its chip — which is
 * what a tenant whose event taxonomy is empty gets, and it is not a state any
 * caller is in today.
 *
 * `reduced` keeps its FIXED width and its centring, and that divergence is the
 * point: its own constant's reason is not a taxonomy fact — the pop-out is
 * 330px wide and gives the type 52 — so a compact column that widened with a
 * firm's longest code would eat a fixed 330.
 */
export default function TypeTagBox({
  children,
  reduced = false,
  mirror = null,
  align = "start",
}: {
  children: ReactNode;
  reduced?: boolean;
  mirror?: ReactNode;
  /** 190 glance (i)(c) — the Bento card reserves its day-counter slot with the
   *  same mirror (the widest label, "Today" localised) and wants the real
   *  label at the slot's END, as a number column reads. `start` is what every
   *  type chip had; nothing else changes. */
  align?: "start" | "end";
}) {
  if (reduced)
    return (
      <span
        style={{
          // inline-FLEX, not inline-block: an inline-block box gives the chip a
          // line box of its own, and the first attempt zeroed its line-height to
          // suppress the descender space — which INHERITED into the chip and
          // crushed it to 4px tall (measured before the fix, on watt's
          // Dashboard). A flex box has no line box to fight.
          display: "inline-flex",
          alignItems: "center",
          // The `<td>`'s own alignment, reduced variant (CaseTaskRow.tsx:453).
          justifyContent: "center",
          width: TYPE_COL_W_REDUCED,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {children}
      </span>
    );
  return (
    <span
      style={{
        display: "inline-grid",
        alignItems: "center",
        /* what `justifyContent: "flex-start"` was on the flex box — and now
           also what stops the chip stretching across the reserved width. */
        justifyItems: align,
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <span style={CELL}>{children}</span>
      {mirror != null && (
        <span aria-hidden="true" style={{ ...CELL, visibility: "hidden" }}>
          {mirror}
        </span>
      )}
    </span>
  );
}
